import { useEffect, useState } from 'react';
import { PostScheduler } from './components/PostScheduler';
import { Auth } from './components/Auth';
import { MFAChallenge } from './components/MFAChallenge';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './lib/AuthProvider';
import { Header } from './components/Header';
import { LinkedInCallback } from './components/LinkedInCallback';
import { GoogleBusinessCallback } from './components/GoogleBusinessCallback';
import { YouTubeCallback } from './components/YouTubeCallback';
import { TikTokCallback } from './components/TikTokCallback';
import { TikTokBusinessCallback } from './components/TikTokBusinessCallback';
import { PinterestCallback } from './components/PinterestCallback';
import { SocialInbox } from './components/social/SocialInbox';
import { MetaOAuthCallback } from './components/social/MetaOAuthCallback';
import { NotFound } from './components/NotFound';
import AdsBooster from './components/ads/AdsBooster';
import PromoteGapli from './components/ads/PromoteGapli';
import { ExpiredTokenBanner } from './components/ExpiredTokenBanner';
import { QuickAccessBar } from './components/QuickAccessBar';
import GapliPromoBanner from './components/GapliPromoBanner';

function AppContent() {
  const { user, mfaRequired, mfaVerified, refreshMfaStatus, signOut } = useAuth();
  const [isLinkedInCallback, setIsLinkedInCallback] = useState(false);
  const [isGoogleBusinessCallback, setIsGoogleBusinessCallback] = useState(false);
  const [isYouTubeCallback, setIsYouTubeCallback] = useState(false);
  const [isTikTokCallback, setIsTikTokCallback] = useState(false);
  const [isTikTokBusinessCallback, setIsTikTokBusinessCallback] = useState(false);
  const [isPinterestCallback, setIsPinterestCallback] = useState(false);
  const [isMetaCallback, setIsMetaCallback] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [currentView, setCurrentView] = useState<'scheduler' | 'social' | 'ads' | 'gapli'>('scheduler');

  useEffect(() => {
    const handleRouteChange = () => {
      const path = window.location.pathname;

      const params = new URLSearchParams(window.location.search);
      const hasCode = params.get('code');
      const hasState = params.get('state');

      const validPaths = [
        '/',
        '/social',
        '/social/accounts',
        '/social/queue',
        '/social/automations',
        '/ads',
        '/gapli',
        '/linkedin-callback',
        '/linkedin/callback',
        '/google-business-callback',
        '/google/callback',
        '/youtube-callback',
        '/youtube/callback',
        '/tiktok-callback',
        '/tiktok/callback',
        '/tiktok-business-callback',
        '/pinterest-callback',
        '/pinterest/callback',
        '/auth/meta/callback',
      ];

      setIsLinkedInCallback(false);
      setIsGoogleBusinessCallback(false);
      setIsYouTubeCallback(false);
      setIsTikTokCallback(false);
      setIsTikTokBusinessCallback(false);
      setIsPinterestCallback(false);
      setIsMetaCallback(false);
      setIsNotFound(false);

      if (path === '/linkedin-callback' || path === '/linkedin/callback') {
        setIsLinkedInCallback(true);
      } else if (path === '/google-business-callback' || path === '/google/callback') {
        setIsGoogleBusinessCallback(true);
      } else if (path === '/youtube-callback' || path === '/youtube/callback') {
        setIsYouTubeCallback(true);
      } else if (path === '/tiktok-callback' || path === '/tiktok/callback') {
        setIsTikTokCallback(true);
      } else if (path === '/tiktok-business-callback') {
        setIsTikTokBusinessCallback(true);
      } else if (path === '/pinterest-callback' || path === '/pinterest/callback') {
        setIsPinterestCallback(true);
      } else if (path === '/auth/meta/callback' || (hasCode && hasState && path === '/social/accounts')) {
        setIsMetaCallback(true);
      } else if (path.startsWith('/social')) {
        setCurrentView('social');
      } else if (path === '/ads') {
        setCurrentView('ads');
      } else if (path === '/gapli') {
        setCurrentView('gapli');
      } else if (path === '/') {
        setCurrentView('scheduler');
      } else if (!validPaths.includes(path) && !path.startsWith('/social/')) {
        setIsNotFound(true);
      } else {
        setCurrentView('scheduler');
      }
    };

    handleRouteChange();

    window.addEventListener('popstate', handleRouteChange);
    window.addEventListener('pushstate', handleRouteChange);
    window.addEventListener('replacestate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('pushstate', handleRouteChange);
      window.removeEventListener('replacestate', handleRouteChange);
    };
  }, []);

  if (isNotFound) {
    return (
      <>
        <Toaster position="top-right" />
        <NotFound
          onNavigateHome={() => {
            setIsNotFound(false);
            setCurrentView('scheduler');
            window.history.pushState({}, '', '/');
          }}
        />
      </>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Toaster position="top-right" />
        <Auth />
      </div>
    );
  }

  if (mfaRequired && !mfaVerified) {
    return (
      <>
        <Toaster position="top-right" />
        <MFAChallenge
          userId={user.id}
          onVerified={refreshMfaStatus}
          onCancel={async () => {
            await signOut();
          }}
        />
      </>
    );
  }

  if (isLinkedInCallback) {
    return (
      <>
        <Toaster position="top-right" />
        <LinkedInCallback
          onComplete={() => {
            setIsLinkedInCallback(false);
            window.history.pushState({}, '', '/');
          }}
        />
      </>
    );
  }

  if (isGoogleBusinessCallback) {
    return (
      <>
        <Toaster position="top-right" />
        <GoogleBusinessCallback
          onComplete={() => {
            setIsGoogleBusinessCallback(false);
            window.history.pushState({}, '', '/');
          }}
        />
      </>
    );
  }

  if (isYouTubeCallback) {
    return (
      <>
        <Toaster position="top-right" />
        <YouTubeCallback />
      </>
    );
  }

  if (isTikTokCallback) {
    return (
      <>
        <Toaster position="top-right" />
        <TikTokCallback />
      </>
    );
  }

  if (isTikTokBusinessCallback) {
    return (
      <>
        <Toaster position="top-right" />
        <TikTokBusinessCallback />
      </>
    );
  }

  if (isPinterestCallback) {
    return (
      <>
        <Toaster position="top-right" />
        <PinterestCallback />
      </>
    );
  }

  if (isMetaCallback) {
    return (
      <>
        <Toaster position="top-right" />
        <MetaOAuthCallback
          onComplete={() => {
            setIsMetaCallback(false);

            const returnUrl = localStorage.getItem('instagram_direct_return_url');
            localStorage.removeItem('instagram_direct_return_url');

            if (returnUrl) {
              const returnPath = new URL(returnUrl, window.location.origin).pathname;
              window.history.pushState({}, '', returnPath);
              setCurrentView(returnPath.startsWith('/social') ? 'social' : 'scheduler');
            } else {
              window.history.pushState({}, '', '/social/accounts');
              setCurrentView('social');
            }
          }}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Toaster position="top-right" />
      <QuickAccessBar />
      <ExpiredTokenBanner />
      <Header onNavigate={(view) => {
        setCurrentView(view);
        const path = view === 'social' ? '/social' : view === 'ads' ? '/ads' : view === 'gapli' ? '/gapli' : '/';
        window.history.pushState({}, '', path);
      }} currentView={currentView} />
      {(currentView === 'scheduler' || currentView === 'social') && (
        <GapliPromoBanner onNavigate={() => {
          setCurrentView('gapli');
          window.history.pushState({}, '', '/gapli');
        }} />
      )}
      {currentView === 'social' ? <SocialInbox /> :
       currentView === 'ads' ? <AdsBooster /> :
       currentView === 'gapli' ? <PromoteGapli /> :
       <PostScheduler />}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;